import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import { EmptyState } from '../components/EmptyState';
import { repository } from '../lib/repository';

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState('All');
  const [searchParams] = useSearchParams();
  const [tag, setTag] = useState(searchParams.get('tag'));

  useEffect(() => { repository.listJournal().then(setArticles); }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(articles.map((a) => a.category)))], [articles]);
  const tags = useMemo(() => Array.from(new Set(articles.flatMap((a) => a.tags))), [articles]);

  const filtered = useMemo(() => {
    let items = [...articles];
    if (category !== 'All') items = items.filter((a) => a.category === category);
    if (tag) items = items.filter((a) => a.tags.includes(tag));
    return items;
  }, [articles, category, tag]);

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="page-journal" className="container py-16 md:py-24">
      <SEO
        title="The Journal"
        description="Essays on cloth, craft, and the wardrobes we keep. Written slowly. Read at leisure."
        path="/journal"
        breadcrumb={[{ to: '/', label: 'Home' }, { label: 'Journal' }]}
      />
      <Breadcrumb items={[{ to: '/', label: 'Home' }, { label: 'Journal' }]} className="mb-8" />
      <p className="overline text-muted-foreground">Editorial</p>
      <h1 className="mt-3 font-display text-6xl md:text-8xl leading-[0.9]">The Journal</h1>
      <p className="mt-6 max-w-xl text-muted-foreground text-lg leading-relaxed">
        Essays on cloth, craft, and the wardrobes we keep. Written slowly. Read at leisure.
      </p>

      {/* Category tabs */}
      <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 overline">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => { setCategory(c); setTag(null); }}
            data-testid={`journal-category-${c.toLowerCase()}`}
            className={`link-underline transition-opacity duration-500 ${category === c ? 'opacity-100' : 'opacity-60 hover:opacity-100'}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Tag rail */}
      <div className="mt-6 flex flex-wrap gap-2">
        {tags.map((t) => (
          <button
            key={t}
            onClick={() => setTag(tag === t ? null : t)}
            data-testid={`journal-tag-${t}`}
            className={`text-xs px-3 py-1.5 border transition-colors duration-500 ${tag === t ? 'bg-foreground text-background border-foreground' : 'border-border hover:border-foreground'}`}
          >
            #{t}
          </button>
        ))}
      </div>

      {/* Featured leader + rest */}
      {filtered.length === 0 ? (
        <EmptyState
          eyebrow="Nothing yet"
          title="No entries match this filter."
          action={<button className="overline link-underline" onClick={() => { setCategory('All'); setTag(null); }}>Reset filter</button>}
          testId="journal-empty"
        />
      ) : (
        <>
          {featured && (
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
              className="mt-16"
              data-testid={`journal-featured-${featured.slug}`}
            >
              <Link to={`/journal/${featured.slug}`} className="group grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                <div className="md:col-span-7 aspect-[4/3] overflow-hidden kb-parent bg-muted">
                  <img src={featured.cover} alt={featured.title} className="kb-img h-full w-full object-cover" />
                </div>
                <div className="md:col-span-5">
                  <p className="overline text-muted-foreground">Featured · {featured.category}</p>
                  <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[0.95]">{featured.title}</h2>
                  <p className="mt-3 font-display italic text-xl text-muted-foreground">{featured.subtitle}</p>
                  <p className="mt-6 text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                  <p className="mt-6 overline text-muted-foreground">
                    {new Date(featured.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {featured.readTime}
                  </p>
                  <span className="mt-6 inline-block overline link-underline">Read the entry</span>
                </div>
              </Link>
            </motion.article>
          )}

          {rest.length > 0 && (
            <div className="mt-24 grid md:grid-cols-3 gap-x-6 gap-y-16">
              {rest.map((a, i) => (
                <motion.article
                  key={a.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.85, 0, 0.15, 1] }}
                  data-testid={`journal-item-${a.slug}`}
                >
                  <Link to={`/journal/${a.slug}`} className="group block">
                    <div className="aspect-[4/5] overflow-hidden kb-parent bg-muted">
                      <img src={a.cover} alt={a.title} className="kb-img h-full w-full object-cover" />
                    </div>
                    <p className="mt-5 overline text-muted-foreground">{a.category} · {a.readTime}</p>
                    <h3 className="mt-3 font-display text-3xl leading-tight">{a.title}</h3>
                    <p className="mt-2 font-display italic text-muted-foreground">{a.subtitle}</p>
                    <div className="mt-4 flex flex-wrap gap-1">
                      {a.tags.map((t) => <span key={t} className="overline text-muted-foreground">#{t}</span>)}
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
